import { TranslationNamespace } from '@/i18n';
import { getTranslated } from '@/i18n/get-translated';

import type { CalculatorInput } from '../constants/defaults';
import { PROFILE_MULTIPLIERS, type ProfileKey } from '../constants/profiles';
import { type RateKey, type ScenarioKey, SETTINGS } from '../constants/settings';

const t = getTranslated(TranslationNamespace.Calculator);

export interface CostGroup {
	label: string;
	now: number;
	consAfter: number;
	consSaving: number;
	posAfter: number;
	posSaving: number;
}

export interface TariffRecommendation {
	name: string;
	price: number;
	description: string;
}

export interface CalculationResult {
	hourly: number;
	groups: CostGroup[];
	tariff: {
		name: string;
		price: number;
		description: string;
	};
	tariffYear: number;
	currentYear: number;
	grossCons: number;
	grossPos: number;
	consAfterYear: number;
	posAfterYear: number;
	consBenefitYear: number;
	posBenefitYear: number;
	consPayback: number | null;
	posPayback: number | null;
}

interface CostItem {
	key: string;
	label: string;
	now: number;
	consSaving?: number;
	posSaving?: number;
	consRate?: number;
	posRate?: number;
	special?: 'ads';
	consAfter?: number;
	posAfter?: number;
}

function num(value: number): number {
	return Math.max(0, Number(value) || 0);
}

function profileMult(key: ProfileKey, activity: CalculatorInput['activity']): number {
	const row = PROFILE_MULTIPLIERS[key];
	return row?.[activity] != null ? row[activity] : 1;
}

function scenarioRate(
	key: RateKey,
	scenario: ScenarioKey,
	activity: CalculatorInput['activity'],
): number {
	return Math.min(1, SETTINGS.rates[key][scenario] * profileMult(key, activity));
}

export function calculate(input: CalculatorInput): CalculationResult {
	const workingDays = SETTINGS.workingDays;
	const hourly = num(input.employeeCost) / SETTINGS.hoursPerMonth;
	const activeMonths = Math.min(12, Math.max(1, num(input.activeMonths)));
	const headcount = Math.max(1, num(input.employees));
	const activity = input.activity;

	const dailyNow = [
		num(input.stockMinutes),
		num(input.reportMinutes),
		num(input.priceMinutes),
		num(input.counterpartyMinutes),
		num(input.communicationMinutes),
		num(input.documentMinutes),
		num(input.otcMinutes),
	];
	const dailyCons = dailyNow.map((n) => n * SETTINGS.time.consShare);
	const dailyPos = dailyNow.map((n) => n * SETTINGS.time.posShare);
	const orderNow = num(input.orderMinutes);
	const orderCons = orderNow * SETTINGS.time.consShare;
	const orderPos = orderNow * SETTINGS.time.posShare;

	const labor = (orderMinutes: number, daily: number[]) =>
		((num(input.orders) * orderMinutes) / 60 +
			(daily.reduce((a, b) => a + b, 0) * workingDays) / 60) *
		hourly *
		activeMonths *
		headcount;

	const laborNow = labor(orderNow, dailyNow);
	const laborCons = labor(orderCons, dailyCons);
	const laborPos = labor(orderPos, dailyPos);

	const volume = num(input.volume);
	const avgPrice = num(input.avgPrice);
	const storageDays = num(input.storageDays);
	const credit = num(input.creditRate) / 100;
	const hasDeferred = input.hasDeferred === 'yes';
	const hasSite = input.hasSite === 'yes';
	const hasMarketer = input.hasMarketer === 'yes';
	const premiumNeed = input.personalManager || input.marketingSupport;

	const rate = (key: RateKey, scenario: ScenarioKey) => scenarioRate(key, scenario, activity);

	const items: CostItem[] = [
		{
			key: 'labor',
			label: t('items.labor'),
			now: laborNow,
			consSaving: Math.max(0, laborNow - laborCons) * profileMult('labor', activity),
			posSaving: Math.max(0, laborNow - laborPos) * profileMult('labor', activity),
		},
		{
			key: 'storage',
			label: t('items.storage'),
			now: volume * storageDays * num(input.storageCost),
			consRate: rate('storage', 'cons'),
			posRate: rate('storage', 'pos'),
		},
		{
			key: 'capital',
			label: t('items.capital'),
			now: ((volume * 1000 * avgPrice * storageDays) / 365) * credit,
			consRate: rate('capital', 'cons'),
			posRate: rate('capital', 'pos'),
		},
		{
			key: 'deferred',
			label: t('items.deferred'),
			now: hasDeferred ? ((num(input.deferredSales) * num(input.deferredDays)) / 365) * credit : 0,
			consRate: rate('deferred', 'cons'),
			posRate: rate('deferred', 'pos'),
		},
		{
			key: 'badDebt',
			label: t('items.bad-debt'),
			now: hasDeferred ? num(input.badDebt) : 0,
			consRate: rate('badDebt', 'cons'),
			posRate: rate('badDebt', 'pos'),
		},
		{
			key: 'expired',
			label: t('items.expired'),
			now: num(input.expiredTons) * 1000 * num(input.expiredLoss),
			consRate: rate('expired', 'cons'),
			posRate: rate('expired', 'pos'),
		},
		{
			key: 'markdown',
			label: t('items.markdown'),
			now: num(input.markdownTons) * 1000 * num(input.markdownLoss),
			consRate: rate('markdown', 'cons'),
			posRate: rate('markdown', 'pos'),
		},
		{
			key: 'weight',
			label: t('items.weight'),
			now: (volume * 1000 * avgPrice * num(input.weightDiff)) / 100,
			consRate: rate('weight', 'cons'),
			posRate: rate('weight', 'pos'),
		},
		{
			key: 'returns',
			label: t('items.returns'),
			now: num(input.returns),
			consRate: rate('returns', 'cons'),
			posRate: rate('returns', 'pos'),
		},
		{
			key: 'site',
			label: t('items.site'),
			now: hasSite ? num(input.siteSupport) * 12 : 0,
			consRate: premiumNeed ? rate('site', 'cons') : 0,
			posRate: premiumNeed ? rate('site', 'pos') : 0,
		},
		{
			key: 'marketer',
			label: t('items.marketer'),
			now: hasMarketer ? num(input.marketerCost) * 12 : 0,
			consRate: premiumNeed ? rate('marketer', 'cons') : 0,
			posRate: premiumNeed ? rate('marketer', 'pos') : 0,
		},
		{
			key: 'ads',
			label: t('items.ads'),
			now: num(input.adBudget) * 12,
			special: 'ads',
		},
		{
			key: 'articles',
			label: t('items.articles'),
			now: num(input.articles) * num(input.articleCost),
			consRate: premiumNeed ? rate('articles', 'cons') : 0,
			posRate: premiumNeed ? rate('articles', 'pos') : 0,
		},
		{
			key: 'ai',
			label: t('items.ai'),
			now: ((num(input.aiMinutes) * workingDays) / 60) * hourly * 12,
			consRate: rate('ai', 'cons'),
			posRate: rate('ai', 'pos'),
		},
		{
			key: 'exhibitions',
			label: t('items.exhibitions'),
			now: num(input.exhibitions) * num(input.exhibitionCost),
			consRate: rate('exhibitions', 'cons'),
			posRate: rate('exhibitions', 'pos'),
		},
		{
			key: 'b2b',
			label: t('items.b2b'),
			now: hasSite ? 0 : num(input.b2bDevelopment) / 3,
			consRate: rate('b2b', 'cons'),
			posRate: rate('b2b', 'pos'),
		},
	];

	items.forEach((item) => {
		if (item.consSaving == null) {
			if (item.special === 'ads') {
				const covered = Math.min(item.now, SETTINGS.platinumAdBudget * 12);
				item.consSaving = premiumNeed ? covered * rate('ads', 'cons') : 0;
				item.posSaving = premiumNeed ? covered * rate('ads', 'pos') : 0;
			} else {
				item.consSaving = item.now * (item.consRate || 0);
				item.posSaving = item.now * (item.posRate || 0);
			}
		}
		item.consAfter = Math.max(0, item.now - (item.consSaving ?? 0));
		item.posAfter = Math.max(0, item.now - (item.posSaving ?? 0));
	});

	const groups: CostGroup[] = [
		{ label: t('items.labor'), keys: ['labor'] },
		{ label: t('groups.storage'), keys: ['storage', 'capital'] },
		{ label: t('groups.deferred'), keys: ['deferred', 'badDebt'] },
		{
			label: t('groups.losses'),
			keys: ['expired', 'markdown', 'weight', 'returns'],
		},
		{
			label: t('groups.marketing'),
			keys: ['site', 'marketer', 'ads', 'articles', 'ai'],
		},
		{
			label: t('groups.exhibitions'),
			keys: ['exhibitions', 'b2b'],
		},
	].map((group) => {
		const selected = items.filter((item) => group.keys.includes(item.key));
		const sum = (prop: 'now' | 'consAfter' | 'consSaving' | 'posAfter' | 'posSaving') =>
			selected.reduce((total, item) => total + (item[prop] ?? 0), 0);
		return {
			label: group.label,
			now: sum('now'),
			consAfter: sum('consAfter'),
			consSaving: sum('consSaving'),
			posAfter: sum('posAfter'),
			posSaving: sum('posSaving'),
		};
	});

	const tariff: TariffRecommendation = premiumNeed
		? {
				name: t('tariff.platinum'),
				price: SETTINGS.tariffs.platinum,
				description: t('tariff.platinum-description'),
			}
		: {
				name: t('tariff.gold'),
				price: SETTINGS.tariffs.gold,
				description: t('tariff.gold-description'),
			};

	const currentYear = items.reduce((total, item) => total + item.now, 0);
	const grossCons = items.reduce((total, item) => total + (item.consSaving ?? 0), 0);
	const grossPos = items.reduce((total, item) => total + (item.posSaving ?? 0), 0);
	const tariffYear = tariff.price * 12;
	const consAfterYear = Math.max(0, currentYear - grossCons + tariffYear);
	const posAfterYear = Math.max(0, currentYear - grossPos + tariffYear);
	const consBenefitYear = currentYear - consAfterYear;
	const posBenefitYear = currentYear - posAfterYear;
	const payback = (gross: number) => (gross > 0 ? (tariff.price / (gross / 12)) * 30 : null);

	return {
		hourly,
		groups,
		tariff,
		tariffYear,
		currentYear,
		grossCons,
		grossPos,
		consAfterYear,
		posAfterYear,
		consBenefitYear,
		posBenefitYear,
		consPayback: payback(grossCons),
		posPayback: payback(grossPos),
	};
}
